import React from 'react';
import { useNavigate } from 'react-router-dom';

// Shared visual building blocks for the super-admin surfaces — keeps the screens
// consistent and away from raw antd defaults. Brand teal #26A3A7.

// Inline teal link used in empty-state hints to send the admin to the section
// where the missing data is configured. `stopPropagation` so clicking it inside a
// Select dropdown / form doesn't also toggle the control.
export const AdminLink: React.FC<{
  to: string;
  children: React.ReactNode;
}> = ({ to, children }) => {
  const navigate = useNavigate();
  return (
    <span
      role="link"
      tabIndex={0}
      className="font-medium text-[#26A3A7] cursor-pointer hover:underline"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        navigate(to);
      }}
    >
      {children}
    </span>
  );
};

export const AdminPageHeader: React.FC<{
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}> = ({ title, subtitle, action }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-4">
    <div>
      <h2 className="text-[22px] font-semibold text-[#18191C] m-0">{title}</h2>
      {subtitle && <p className="text-[#84919A] text-[14px] m-0">{subtitle}</p>}
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const StatusPill: React.FC<{ active: boolean }> = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[12px] font-medium ${
      active ? 'bg-[#E6F4F4] text-[#1A8B8F]' : 'bg-[#FDECEC] text-[#C0392B]'
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? 'bg-[#1A8B8F]' : 'bg-[#C0392B]'
      }`}
    />
    {active ? 'Active' : 'Inactive'}
  </span>
);

// Square avatar: shows the user's photo when available, otherwise the first
// letter of their name. Falls back to the initial if the image fails to load
// (e.g. a stale/private URL) so the cell never renders a broken-image icon.
export const Initial: React.FC<{ name?: string; src?: string }> = ({
  name,
  src,
}) => {
  const [errored, setErrored] = React.useState(false);
  const letter = (name ?? '?').trim().charAt(0).toUpperCase();
  if (src && !errored) {
    return (
      <img
        src={src}
        alt={name ?? ''}
        onError={() => setErrored(true)}
        className="w-8 h-8 rounded-md object-cover shrink-0"
      />
    );
  }
  return (
    <div className="w-8 h-8 rounded-md bg-[#E6F4F4] text-[#26A3A7] flex items-center justify-center text-[14px] font-semibold shrink-0">
      {letter}
    </div>
  );
};

export const cardShadow = { boxShadow: '0px 1px 2px rgba(16,24,40,0.05)' };
export const TEAL = '#26A3A7';
